import { apiClient } from '@/plugins/axios/api'
import type { CommuneData } from '@/views/map/components/CommuneCard.vue'

/**
 * Interface pour un exécutif (maire, adjoint, etc.)
 */
export interface CommuneExecutif {
  '@context'?: string
  '@id'?: string
  '@type'?: string
  id: string
  excNom?: string
  excEmail?: string
  excTelephone?: string
  excRole?: string
  excParti?: string
  excSexe?: string
  excAnneeDebut?: number
  excAnneeFin?: number
}

/**
 * Hydra collection response for /api/communes
 */
interface CommuneCollectionResponse {
  'hydra:member'?: CommuneApiResponse[]
  'hydra:totalItems'?: number
}

/** GeoJSON Polygon or MultiPolygon from API (full commune boundary). */
export type CommuneGeometry = GeoJSON.Polygon | GeoJSON.MultiPolygon

/**
 * Interface pour les données complètes d'une commune depuis l'API
 */
export interface CommuneApiResponse {
  id?: string
  cmnCode?: string
  cmnTitre?: string
  cmnDept?: string
  cmnRegion?: string
  cmnPopulation?: number
  cmnDateCreation?: string
  cmnBoitePostale?: string
  cmnSiteWeb?: string
  cmnSuperficie?: string | number
  cmnDensite?: string
  cmnEmailMarie?: string
  cmnNbrConseiller?: number
  cmnNbrConseillerFemme?: number
  cmnNbrAdjoint?: number
  cmnNbrAdjointFemme?: number
  cmnExecutifs?: CommuneExecutif[]
  /** Full commune boundary (GeoJSON). API may use geometry, geo, or boundary. */
  geometry?: CommuneGeometry
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any
}

/**
 * Extracts GeoJSON Polygon or MultiPolygon from API response.
 * Supports common field names: geometry, geo, boundary.
 */
export function getCommuneGeometryFromApi(api: CommuneApiResponse | null): CommuneGeometry | null {
  if (!api) return null
  const raw =
    (api.geometry ?? (api as Record<string, unknown>).geo ?? (api as Record<string, unknown>).boundary) as
    | GeoJSON.Polygon
    | GeoJSON.MultiPolygon
    | undefined
  if (!raw || typeof raw !== 'object') return null
  if (raw.type === 'Polygon' || raw.type === 'MultiPolygon') return raw
  return null
}

/**
 * Maps API Commune resource (camelCase) to CommuneData (snake_case) for the fiche card.
 * Fills maireInfo from the executif whose excRole is exactly "Maire" (cmnExecutifs is empty for
 * the ~37 communes with no known mayor — maireInfo/cmn_maire stay undefined in that case).
 */
export function mapApiResponseToCommuneData(api: CommuneApiResponse): CommuneData {
  const maire = api.cmnExecutifs?.find((exec) => exec.excRole?.trim().toLowerCase() === 'maire')

  const maireInfo =
    maire ?
      {
        nom: maire.excNom,
        email: maire.excEmail,
        telephone: maire.excTelephone,
        role: maire.excRole,
        parti: maire.excParti,
        sexe: maire.excSexe,
      }
    : undefined

  return {
    id: api.id,
    cmn_code: api.cmnCode,
    cmn_titre: api.cmnTitre,
    cmn_dept: api.cmnDept,
    cmn_region: api.cmnRegion,
    cmn_population: api.cmnPopulation,
    cmn_statut: undefined,
    cmn_boite_postale: api.cmnBoitePostale,
    cmn_site_web: api.cmnSiteWeb,
    cmn_superficie:
      api.cmnSuperficie !== undefined && api.cmnSuperficie !== null
        ? Number(api.cmnSuperficie)
        : undefined,
    cmn_densite: api.cmnDensite,
    cmn_email_marie: api.cmnEmailMarie ?? maire?.excEmail,
    cmn_maire: maire?.excNom,
    cmn_telephone: maire?.excTelephone,
    cmn_nbr_conseiller: api.cmnNbrConseiller,
    cmn_nbr_conseiller_femme: api.cmnNbrConseillerFemme,
    cmn_nbr_adjoint: api.cmnNbrAdjoint,
    cmn_nbr_adjoint_femme: api.cmnNbrAdjointFemme,
    cmn_date_creation: api.cmnDateCreation,
    cmn_annee_debut: maire?.excAnneeDebut,
    cmn_annee_fin: maire?.excAnneeFin,
    maireInfo,
    nom: api.cmnTitre,
    departement: api.cmnDept,
    region: api.cmnRegion,
    maire: maire?.excNom,
    habitants: api.cmnPopulation,
    superficie:
      api.cmnSuperficie !== undefined && api.cmnSuperficie !== null
        ? Number(api.cmnSuperficie)
        : undefined,
    densite: api.cmnDensite,
  }
}

/**
 * Service pour gérer les données des communes depuis l'API
 */
export class CommuneService {
  /**
   * Récupère les données d'une commune par son ID
   * @param communeId ID de la commune
   * @returns Promise<CommuneApiResponse> Données de la commune avec les exécutifs
   */
  static async getCommuneById(communeId: string): Promise<CommuneApiResponse | null> {
    try {
      const response = await apiClient.get(`/api/communes/${communeId}`)
      return response.data as CommuneApiResponse
    } catch (error) {
      console.error(`Erreur lors de la récupération de la commune ${communeId}:`, error)
      return null
    }
  }

  /**
   * Fetches all communes from the API, paginating until all pages are retrieved.
   * Returns communes sorted alphabetically by region, department, title.
   */
  static async getAllCommunes(): Promise<CommuneApiResponse[]> {
    const PAGE_SIZE = 100
    try {
      const firstResponse = await apiClient.get<CommuneCollectionResponse>('/api/communes', {
        params: { itemsPerPage: PAGE_SIZE, page: 1 },
      })
      const totalItems = firstResponse.data['hydra:totalItems'] ?? 0
      const firstPage = firstResponse.data['hydra:member'] ?? []
      if (totalItems <= PAGE_SIZE) return firstPage as CommuneApiResponse[]

      const totalPages = Math.ceil(totalItems / PAGE_SIZE)
      const pageRequests = Array.from({ length: totalPages - 1 }, (_, i) =>
        apiClient.get<CommuneCollectionResponse>('/api/communes', {
          params: { itemsPerPage: PAGE_SIZE, page: i + 2 },
        }),
      )
      const pages = await Promise.all(pageRequests)
      const rest = pages.flatMap((r) => (r.data['hydra:member'] ?? []) as CommuneApiResponse[])
      const all = [...(firstPage as CommuneApiResponse[]), ...rest]
      const seen = new Set<string>()
      return all.filter((c) => {
        const key = c.cmnCode ?? c.id ?? c.cmnTitre ?? JSON.stringify(c)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      })
    } catch (error) {
      console.error('Erreur lors du chargement de toutes les communes:', error)
      return []
    }
  }

  /**
   * Searches communes by name (cmnTitre) via API Platform filter.
   * @param query Search string (partial match on cmnTitre)
   * @param limit Max results (default 15)
   */
  static async searchCommunes(
    query: string,
    limit = 15,
  ): Promise<CommuneApiResponse[]> {
    if (!query || String(query).trim() === '') {
      return []
    }
    try {
      const response = await apiClient.get<CommuneCollectionResponse>('/api/communes', {
        params: { cmnTitre: String(query).trim(), itemsPerPage: limit },
      })
      const members = response.data['hydra:member']
      return Array.isArray(members) ? (members as CommuneApiResponse[]) : []
    } catch (error) {
      console.error(`Erreur lors de la recherche de communes (${query}):`, error)
      return []
    }
  }

  /**
   * Fetches a commune by its code (e.g. CM002009001).
   * The collection endpoint (filtered by cmnCode) doesn't carry cmnExecutifs, so once the
   * commune's id is resolved from it, this chains to the item endpoint for the full record
   * (maire, etc.). Needed on the central geoportal, whose vector tiles only carry cmn_code,
   * never the commune's UUID — falls back to the shallow collection member if that second call fails.
   */
  static async getCommuneByCode(code: string): Promise<CommuneApiResponse | null> {
    try {
      const response = await apiClient.get<CommuneCollectionResponse>('/api/communes', {
        params: { cmnCode: code },
      })
      const members = response.data['hydra:member']
      if (!Array.isArray(members) || members.length === 0) {
        return null
      }
      const shallow = members[0] as CommuneApiResponse
      if (shallow.id) {
        const full = await this.getCommuneById(shallow.id)
        if (full) return full
      }
      return shallow
    } catch (error) {
      console.error(`Erreur lors de la récupération de la commune par code ${code}:`, error)
      return null
    }
  }

  /**
   * Récupère le maire d'une commune depuis les exécutifs
   * @param communeId ID de la commune
   * @returns Promise<CommuneExecutif | null> Le maire ou null si non trouvé
   */
  static async getMaireByCommuneId(communeId: string): Promise<CommuneExecutif | null> {
    try {
      const commune = await this.getCommuneById(communeId)
      if (!commune || !commune.cmnExecutifs || commune.cmnExecutifs.length === 0) {
        return null
      }

      // Chercher le maire par rôle (rôle exact "Maire" — le tableau peut contenir d'autres rôles, ex. adjoints, à l'avenir)
      const maire = commune.cmnExecutifs.find((exec) => exec.excRole?.trim().toLowerCase() === 'maire')

      return maire ?? null
    } catch (error) {
      console.error(`Erreur lors de la récupération du maire pour la commune ${communeId}:`, error)
      return null
    }
  }

  /**
   * Récupère tous les exécutifs d'une commune
   * @param communeId ID de la commune
   * @returns Promise<CommuneExecutif[]> Liste des exécutifs
   */
  static async getExecutifsByCommuneId(communeId: string): Promise<CommuneExecutif[]> {
    try {
      const commune = await this.getCommuneById(communeId)
      return commune?.cmnExecutifs || []
    } catch (error) {
      console.error(
        `Erreur lors de la récupération des exécutifs pour la commune ${communeId}:`,
        error,
      )
      return []
    }
  }
}
